/**
 * Exercise clips on the device's disk (`D194`), so the next exercise of a
 * workout plays offline.
 *
 * ── WHAT IT DOES ───────────────────────────────────────────────────────────
 * Before a workout the session's clips are DOWNLOADED into the app's private
 * data directory (`Filesystem.downloadFile`: native, so the CDN needs no CORS
 * answer), and a clip on disk is played from there through
 * `Capacitor.convertFileSrc` — `https://localhost/_capacitor_file_/…`. Until
 * this module the clips lived only in the WebView's HTTP cache, which is
 * filled by playing them: the next exercise's clip was first requested when
 * it became current, and offline it simply never played.
 *
 * ── WHEN THE SOURCE IS DECIDED ─────────────────────────────────────────────
 * {@link localClipSrc} is read ONCE, when the `<video>` mounts, and never
 * reassigned (`T-155`: assigning `src` reloads, even to the same value). A
 * clip that lands on disk after its player mounted keeps playing from the
 * CDN until the next mount; that is the price of never reloading.
 *
 * ── HOW MUCH ───────────────────────────────────────────────────────────────
 * At most {@link MAX_CLIPS} files (~177 KB each, a ~10 MB ceiling), the ones
 * least recently played or cached evicted first. The index is one JSON file
 * beside them; one that is lost or corrupt is an empty cache, and the files
 * it named are overwritten when cached again.
 *
 * The web has no files: everything here is a no-op there, and the web build
 * warms the next clip in the HTTP cache instead ({@link warmClip}).
 */
import { isNative } from "./platform";

/** The most clips kept on disk. */
export const MAX_CLIPS = 60;

const DIRECTORY = "clips";
const INDEX_PATH = `${DIRECTORY}/index.json`;

/** One cached clip: where it is, and when it was last wanted. */
export interface CachedClip {
  readonly uri: string;
  readonly usedAt: number;
}

export type ClipIndex = Readonly<Record<string, CachedClip>>;

/** The files the cache needs; the Capacitor plugin on the device. */
export interface ClipFiles {
  delete(path: string): Promise<void>;
  /** Download `url` to `path`; resolves to the file's URI. */
  download(url: string, path: string): Promise<string>;
  readIndex(): Promise<string | null>;
  toSrc(uri: string): string;
  writeIndex(json: string): Promise<void>;
}

const capacitorFiles: ClipFiles = {
  delete: async (path) => {
    const { Directory, Filesystem } = await import("@capacitor/filesystem");
    await Filesystem.deleteFile({ directory: Directory.Data, path }).catch(
      () => undefined
    );
  },
  download: async (url, path) => {
    const { Directory, Filesystem } = await import("@capacitor/filesystem");
    // `recursive` does NOT create the parent on Android (the plugin's legacy
    // download opens the file and fails ENOENT), so the folder is made
    // first; an existing one rejects, which is the folder being there.
    await Filesystem.mkdir({
      directory: Directory.Data,
      path: DIRECTORY,
      recursive: true,
    }).catch(() => undefined);
    // Deprecated for `@capacitor/file-transfer`, which is a native plugin
    // this shell does not carry; adding it would need a new APK.
    await Filesystem.downloadFile({
      directory: Directory.Data,
      path,
      recursive: true,
      url,
    });
    const { uri } = await Filesystem.getUri({
      directory: Directory.Data,
      path,
    });
    return uri;
  },
  readIndex: async () => {
    const { Directory, Encoding, Filesystem } = await import(
      "@capacitor/filesystem"
    );
    const read = await Filesystem.readFile({
      directory: Directory.Data,
      encoding: Encoding.UTF8,
      path: INDEX_PATH,
    }).catch(() => null);
    return typeof read?.data === "string" ? read.data : null;
  },
  toSrc: (uri) => {
    const capacitor = (
      globalThis as { Capacitor?: { convertFileSrc(path: string): string } }
    ).Capacitor;
    return capacitor?.convertFileSrc(uri) ?? uri;
  },
  writeIndex: async (json) => {
    const { Directory, Encoding, Filesystem } = await import(
      "@capacitor/filesystem"
    );
    await Filesystem.writeFile({
      data: json,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
      path: INDEX_PATH,
      recursive: true,
    });
  },
};

function parseIndex(json: string | null): ClipIndex {
  if (json === null) {
    return {};
  }
  try {
    const value = JSON.parse(json) as unknown;
    if (typeof value !== "object" || value === null) {
      return {};
    }
    const index: Record<string, CachedClip> = {};
    for (const [hash, entry] of Object.entries(value)) {
      const { uri, usedAt } = (entry ?? {}) as Partial<CachedClip>;
      if (typeof uri === "string" && typeof usedAt === "number") {
        index[hash] = { uri, usedAt };
      }
    }
    return index;
  } catch {
    return {};
  }
}

/** The entries past `max`, least recently used first. */
export function evictions(index: ClipIndex, max: number): string[] {
  const hashes = Object.keys(index);
  if (hashes.length <= max) {
    return [];
  }
  return hashes
    .sort((a, b) => (index[a]?.usedAt ?? 0) - (index[b]?.usedAt ?? 0))
    .slice(0, hashes.length - max);
}

export function createClipCache(
  files: ClipFiles,
  url: (hash: string) => string,
  now: () => number = Date.now,
  max: number = MAX_CLIPS
) {
  let index: Record<string, CachedClip> = {};
  let loaded: Promise<void> | null = null;
  let chain: Promise<unknown> = Promise.resolve();

  const save = () => files.writeIndex(JSON.stringify(index));

  const load = (): Promise<void> => {
    loaded ??= files.readIndex().then(
      (json) => {
        index = { ...parseIndex(json), ...index };
      },
      () => undefined
    );
    return loaded;
  };

  /** Serialised, so two caching passes never interleave their index writes. */
  const run = (work: () => Promise<void>): Promise<void> => {
    const next = chain.then(work, work);
    chain = next.catch(() => undefined);
    return next;
  };

  return {
    load,
    /**
     * Download every clip not already on disk, then evict past the ceiling.
     * A clip that fails to download is skipped — the CDN still serves it —
     * and the rest go on.
     */
    cache(hashes: readonly string[]): Promise<void> {
      return run(async () => {
        await load();
        let changed = false;
        for (const hash of new Set(hashes)) {
          const held = index[hash];
          if (held !== undefined) {
            index[hash] = { ...held, usedAt: now() };
            changed = true;
            continue;
          }
          try {
            const uri = await files.download(
              url(hash),
              `${DIRECTORY}/${hash}.mp4`
            );
            index[hash] = { uri, usedAt: now() };
            changed = true;
          } catch (error) {
            console.warn("[media] a clip was not cached", hash, error);
          }
        }
        for (const hash of evictions(index, max)) {
          await files.delete(`${DIRECTORY}/${hash}.mp4`);
          delete index[hash];
          changed = true;
        }
        if (changed) {
          await save().catch(() => undefined);
        }
      });
    },
    /** The disk copy as a playable URL, or `null` (see the header). */
    src(hash: string): string | null {
      const held = index[hash];
      return held === undefined ? null : files.toSrc(held.uri);
    },
  };
}

let cacheInstance: ReturnType<typeof createClipCache> | null = null;

function instance(url: (hash: string) => string) {
  cacheInstance ??= createClipCache(capacitorFiles, url);
  return cacheInstance;
}

/** Read the index from disk; boot calls it early so mounts find it. */
export function loadClipCache(url: (hash: string) => string): Promise<void> {
  return isNative() ? instance(url).load() : Promise.resolve();
}

/** Download these clips for the workout ahead. Never throws; web: nothing. */
export function cacheClips(
  hashes: readonly string[],
  url: (hash: string) => string
): Promise<void> {
  return isNative()
    ? instance(url)
        .cache(hashes)
        .catch(() => undefined)
    : Promise.resolve();
}

/** The disk copy of a clip, or `null` — read once, when a player mounts. */
export function localClipSrc(hash: string): string | null {
  return cacheInstance?.src(hash) ?? null;
}

/**
 * Warm one clip in the HTTP cache — the web's answer, and the device's for a
 * clip not on disk yet: the next exercise's clip is fetched while this one
 * is played, so it starts at once when it becomes current. Low priority,
 * never awaited, and a failure is only a colder start.
 */
export function warmClip(url: string): void {
  fetch(url, { mode: "no-cors", priority: "low" } as RequestInit).catch(
    () => undefined
  );
}
